import React from 'react';
import { Users, Award, Handshake, Star, TrendingUp, Shield } from 'lucide-react';

/**
 * Phase 16 — Trust Signals UI Block
 *
 * Renders a compact "trust bar" with 4 key signals:
 *   - Clients (4,000+ brands)
 *   - Experts (120+ specialists, founder-led)
 *   - Partners (30+ marketplaces)
 *   - Awards / Ratings (4.9/5)
 *
 * Each stat is data-testid'd for LLM extraction + tests.
 */
const TrustSignals = ({ variant = 'full', className = '' }) => {
  const signals = [
    { icon: Users, label: 'Brands Onboarded', value: '4,000+', sub: 'Since 2016', testid: 'trust-brands' },
    { icon: Handshake, label: 'Marketplaces Covered', value: '30+', sub: 'India, UAE, USA', testid: 'trust-marketplaces' },
    { icon: TrendingUp, label: 'Team Size', value: '120+', sub: 'Category specialists', testid: 'trust-team' },
    { icon: Star, label: 'Rating', value: '4.9 / 5', sub: '200+ verified reviews', testid: 'trust-rating' },
  ];

  if (variant === 'bar') {
    return (
      <div className={`flex flex-wrap items-center justify-center gap-x-8 gap-y-3 py-4 ${className}`} data-testid="trust-signals-bar">
        {signals.map((s) => (
          <div key={s.testid} className="flex items-center gap-2 text-sm" data-testid={s.testid}>
            <s.icon className="w-4 h-4 text-pink-500" />
            <span className="font-semibold text-slate-900">{s.value}</span>
            <span className="text-slate-500">{s.label}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <section className={`py-10 ${className}`} data-testid="trust-signals">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {signals.map((s) => (
          <div
            key={s.testid}
            className="rounded-2xl border border-slate-200 bg-white p-5 text-center hover:border-pink-300 hover:shadow-md transition-all"
            data-testid={s.testid}
          >
            <div className="w-11 h-11 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center mx-auto mb-3">
              <s.icon className="w-5 h-5" />
            </div>
            <div className="text-3xl font-bold text-slate-900 mb-0.5">{s.value}</div>
            <div className="text-sm font-medium text-slate-700">{s.label}</div>
            <div className="text-xs text-slate-500 mt-0.5">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Awards + Certifications row */}
      <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs text-slate-500">
        <div className="inline-flex items-center gap-1.5"><Award className="w-3.5 h-3.5 text-amber-500" /> ET Retail Awards 2024 · Nominee</div>
        <div className="inline-flex items-center gap-1.5"><Award className="w-3.5 h-3.5 text-amber-500" /> Best Marketplace Partner 2023 · IRF</div>
        <div className="inline-flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-emerald-500" /> DPDPA Act 2023 Compliant</div>
        <div className="inline-flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-emerald-500" /> ISO 27001 (in-progress)</div>
      </div>
    </section>
  );
};

export default TrustSignals;
